import React, { useState, useMemo, useEffect } from 'react'; // Import useEffect
import {
  TextField,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog, // Import Dialog for the pop-up
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import axiosInstance from '../axiosInstance';

// Retrieve API URL from environment variables.
// This should be set up in your .env.local file (e.g., REACT_APP_API_URL=http://localhost:3001)
const apiUrl = process.env.REACT_APP_API_URL;

// Main React Component for the Invoice Validation Tool
const InvoiceCheckPage = () => {
  // State for the PO Number input, pre-filled with 'J' as required.
  const [poNumber, setPoNumber] = useState('J');
  // State for the processed (cleaned and uppercased) PO Number after submission.
  const [processedPoNumber, setProcessedPoNumber] = useState('');
  // State to store product receipts data fetched from RetailOps (via backend).
  const [retailOpsData, setRetailOpsData] = useState(null);
  // State to store prior invoice records fetched from the internal DB (via backend).
  const [priorInvoices, setPriorInvoices] = useState(null);
  // State to manage loading indicators during API calls.
  const [isLoading, setIsLoading] = useState(false);
  // State to display user messages (success, error, info, warning).
  const [message, setMessage] = useState({ type: '', text: '' });

  // State to hold unique identifiers of selected line items for the current invoice accumulation.
  // Each ID is typically a string like `${item.sku}-${item.lot}-${item.receivedDate}`.
  const [selectedLineItems, setSelectedLineItems] = useState([]);

  // State to control the visibility of the prior invoice warning modal.
  const [showPriorInvoiceModal, setShowPriorInvoiceModal] = useState(false);
  // State to store the specific prior invoice details to show in the modal, filtered by selected items.
  const [priorInvoiceModalContent, setPriorInvoiceModalContent] = useState([]);

  /**
   * Memoized set of lot IDs that have been previously invoiced for the current PO.
   * Used for quick lookup to determine if an item is already invoiced.
   */
  const previouslyInvoicedLotIdsSet = useMemo(() => {
    if (!priorInvoices) return new Set();
    return new Set(priorInvoices.map(inv => inv.lot_id));
  }, [priorInvoices]);

  /**
   * Helper function to check if a specific item (by its lot ID) has been previously invoiced.
   * @param {object} item - The item object from retailOpsData.
   * @returns {boolean} True if the item's lot ID is in the set of previously invoiced lot IDs.
   */
  const isItemPreviouslyInvoiced = (item) => {
    return previouslyInvoicedLotIdsSet.has(item.lot);
  };

  /**
   * Memoized calculation of the current invoice's accumulated total quantity and value.
   * This sum is purely for display in the "Current Invoice Accumulation" section.
   * The actual storage will send individual line item data based on these selections.
   */
  const currentInvoiceSelected = useMemo(() => {
    // If no retail operations data or no items are selected, return zeros.
    if (!retailOpsData || selectedLineItems.length === 0) {
      return { quantity: 0, value: 0 };
    }

    // Filter the complete retailOpsData to include only the items whose unique IDs are in selectedLineItems.
    const filteredItems = retailOpsData.filter(item =>
      selectedLineItems.includes(`${item.sku}-${item.lot}-${item.receivedDate}`)
    );

    // Calculate the sum of quantities for the selected items.
    const quantity = filteredItems.reduce((sum, item) => sum + item.quantityReceived, 0);
    // Calculate the sum of line totals for the selected items.
    const value = filteredItems.reduce((sum, item) => sum + item.lineTotal, 0);
    
    // Return the aggregated quantity and value.
    return { quantity, value };
  }, [retailOpsData, selectedLineItems]); // Recalculate when source data or selections change.

  /**
   * Memoized grouping of retail operations data by received date.
   * This is used to display items organized by date in collapsible Accordions.
   */
  const groupedDataByDate = useMemo(() => {
    if (!retailOpsData) return {};
    return retailOpsData.reduce((acc, item) => {
      const date = item.receivedDate;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});
  }, [retailOpsData]);

  /**
   * Memoized calculation of the grand total quantity for all retail operations data for the PO.
   */
  const grandTotalQuantity = useMemo(() => {
    return retailOpsData ? retailOpsData.reduce((sum, item) => sum + item.quantityReceived, 0) : 0;
  }, [retailOpsData]);

  /**
   * Memoized calculation of the grand total value for all retail operations data for the PO.
   */
  const grandTotalValue = useMemo(() => {
    return retailOpsData ? retailOpsData.reduce((sum, item) => sum + item.lineTotal, 0) : 0;
  }, [retailOpsData]);

  /**
   * Handles changes to the PO Number input field.
   * @param {Object} event - The DOM event object.
   */
  const handlePoNumberChange = (event) => {
    setPoNumber(event.target.value);
  };

  /**
   * Handles the submission of the PO number.
   * Fetches product receipts from the backend (which queries RetailOps)
   * and any prior invoice records from the internal database.
   * Clears previous results and messages before a new submission.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true); // Activate loading indicator
    setMessage({ type: '', text: '' }); // Clear any previous messages
    setRetailOpsData(null); // Clear previous retail ops data
    setPriorInvoices(null); // Clear previous prior invoices
    setSelectedLineItems([]); // Clear any previously selected items
    setShowPriorInvoiceModal(false); // Close modal on new search

    // Clean the PO number: remove whitespace and convert to uppercase.
    const cleanedPo = poNumber.replace(/\s/g, '').toUpperCase();
    setProcessedPoNumber(cleanedPo); // Store the processed PO number

    // Validate if the API URL is defined in environment variables.
    if (!apiUrl) {
      setMessage({ type: 'error', text: 'Backend API URL is not defined. Please set REACT_APP_API_URL in your environment.' });
      setIsLoading(false); // Deactivate loading indicator
      return;
    }

    try {
      // Retrieve the authentication token from local storage.
      const token = localStorage.getItem("authToken"); 
      // Make a GET request to your backend API to fetch PO data and prior invoices.
      const response = await axiosInstance.get(`${apiUrl}/api/po-data/${cleanedPo}`, {
        headers: { Authorization: `Bearer ${token}` }, // Include authentication token in headers.
      });

      // Update state with the fetched data.
      setRetailOpsData(response.data.retailOpsData);
      setPriorInvoices(response.data.priorInvoices);

      // Display an informational message if no product receipts were found.
      if (!response.data.retailOpsData || response.data.retailOpsData.length === 0) {
        setMessage({ type: 'info', text: `No product receipts found for PO number: ${cleanedPo}` });
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      // Display an error message to the user, attempting to get a specific message from the error response.
      setMessage({
        type: 'error',
        text: `Error fetching data: ${error.response?.data?.message || error.message || 'Network error'}`
      });
    } finally {
      setIsLoading(false); // Always deactivate loading indicator, regardless of success or failure.
    }
  };

  /**
   * Handles the storage of selected invoice line items to the backend.
   * This function now constructs an array of selected items and sends it in a single POST request.
   */
  const handleStoreInvoice = async () => {
    // Filter the complete retailOpsData to get only the items marked for invoicing.
    // Also, ensure that only items not previously invoiced are sent to the backend.
    const itemsToInvoice = retailOpsData.filter(item =>
      selectedLineItems.includes(`${item.sku}-${item.lot}-${item.receivedDate}`) &&
      !isItemPreviouslyInvoiced(item) // Only include items not previously invoiced
    );

    // Prevent storage if no items have been selected or all selected items were already invoiced.
    if (itemsToInvoice.length === 0) {
      setMessage({ type: 'warning', text: 'No new items selected for this invoice or all selected items have been previously invoiced.' });
      return;
    }

    setIsLoading(true); // Activate loading indicator
    setMessage({ type: '', text: '' }); // Clear previous messages
    setShowPriorInvoiceModal(false); // Close modal if open before storing

    // Validate if the API URL is defined.
    if (!apiUrl) {
      setMessage({ type: 'error', text: 'Backend API URL is not defined. Please set REACT_APP_API_URL in your environment.' });
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken"); // Get the authentication token

      // Prepare the payload as an array of objects, each representing an invoice line item.
      const payload = itemsToInvoice.map(item => ({
        poNumber: processedPoNumber,
        totalQuantity: item.quantityReceived, // Individual item's quantity
        totalValue: item.lineTotal,         // Individual item's line total
        lotId: item.lot,                    // Individual item's lot ID
      }));

      // Send a single POST request with the array of invoice items.
      const response = await axiosInstance.post(`${apiUrl}/api/store-invoice`, payload, {
        headers: { Authorization: `Bearer ${token}` }, // Include token in headers
      });

      // Handle the response from the backend.
      if (response.status === 201) {
        setMessage({ type: 'success', text: `${response.data.details.successful.length} invoice record(s) stored successfully for PO ${processedPoNumber}.` });
      } else if (response.status === 207) { // Partial success (some stored, some failed)
        setMessage({ type: 'warning', text: `Successfully stored ${response.data.details.successful.length} record(s), but ${response.data.details.failed.length} failed. See console for details.` });
        console.warn('Partial storage success details:', response.data.details);
      } else {
        // Fallback for unexpected successful status codes
        setMessage({ type: 'info', text: `Invoice storage completed with status: ${response.status}. See console for details.` });
      }

      // Reset the form and data to a fresh state after attempting to store all items.
      // This returns the user to the start screen as per requirements.
      setPoNumber('J');
      setProcessedPoNumber('');
      setRetailOpsData(null);
      setPriorInvoices(null);
      setSelectedLineItems([]); // Clear selected items after storing

    } catch (error) {
      console.error('Error storing invoices:', error);
      // Handle different types of errors from the API call.
      if (axios.isAxiosError(error)) {
        if (error.response) {
          setMessage({
            type: 'error',
            text: `Error storing invoices: ${error.response.data.message || 'Unknown error'}`,
          });
          if (error.response.data.details) {
            console.error('Invoice storage error details:', error.response.data.details);
          }
        } else if (error.request) {
          setMessage({ type: 'error', text: 'Network error: Invoice storage API not responding.' });
        } else {
          setMessage({ type: 'error', text: `Request setup error: ${error.message}` });
        }
      } else {
        setMessage({ type: 'error', text: `An unexpected error occurred during invoice storage: ${error.message || 'Unknown error'}` });
      }
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  /**
   * Toggles the selection state of individual line items or an entire group of items for a given date/PO.
   * Updates `selectedLineItems` state and provides user feedback via `message`.
   * Also triggers the "Prior Invoices" modal if a selected item has a prior record.
   * @param {object|Array<object>} itemsToToggle - The item object or an array of item objects to toggle.
   * @param {boolean} [isGroupToggle=false] - True if toggling a whole date group or PO total, false for individual item.
   */
  const toggleSelection = (itemsToToggle, isGroupToggle = false) => {
    setSelectedLineItems(prevSelected => {
      let newSelected = [...prevSelected];
      let action = ''; // To indicate 'added' or 'removed' in the message

      // Convert single item or array of items into an array of their unique IDs
      // Ensure we always work with an array of items
      const itemsArray = isGroupToggle ? itemsToToggle : [itemsToToggle];

      // Filter out items that are already previously invoiced from the group to be toggled
      const actionableItems = itemsArray.filter(item => !isItemPreviouslyInvoiced(item));
      const actionableItemIds = actionableItems.map(item => `${item.sku}-${item.lot}-${item.receivedDate}`);

      // If all original items were already invoiced, don't allow any action
      if (actionableItems.length === 0 && itemsArray.length > 0) {
        setMessage({ type: 'warning', text: `All selected item(s) are already invoiced.` });
        setShowPriorInvoiceModal(true); // Still show modal if they click on an already invoiced item/group
        setPriorInvoiceModalContent(priorInvoices.filter(inv => itemsArray.some(item => item.lot === inv.lot_id)));
        return prevSelected; // Do not modify selected items
      }

      if (isGroupToggle) {
        const allActionableSelected = actionableItemIds.every(id => prevSelected.includes(id));

        if (allActionableSelected) {
          // If all *actionable* items are selected, unselect them
          newSelected = newSelected.filter(id => !actionableItemIds.includes(id));
          action = 'removed';
        } else {
          // If not all *actionable* items are selected, select all of them
          actionableItemIds.forEach(id => {
            if (!newSelected.includes(id)) {
              newSelected.push(id);
            }
          });
          action = 'added';
        }
      } else { // Logic for individual item toggle
        const itemId = actionableItemIds[0]; // Only one item for individual toggle
        
        if (newSelected.includes(itemId)) {
          newSelected = newSelected.filter(id => id !== itemId);
          action = 'removed';
        } else {
          newSelected.push(itemId);
          action = 'added';
        }
      }

      // After updating selected items, calculate new totals for the user feedback message
      const currentFilteredItems = retailOpsData.filter(item =>
        newSelected.includes(`${item.sku}-${item.lot}-${item.receivedDate}`)
      );
      const newQuantity = currentFilteredItems.reduce((sum, item) => sum + item.quantityReceived, 0);
      const newValue = currentFilteredItems.reduce((sum, item) => sum + item.lineTotal, 0);

      const itemDescription = isGroupToggle ? (itemsArray.length > 0 ? `items from ${itemsArray[0].receivedDate || 'PO'}` : 'PO items') : `${itemsArray[0].sku || 'selected item'}`;
      setMessage({ type: 'info', text: `${action === 'added' ? 'Added' : 'Removed'} ${itemDescription}. Current invoice total: ${newQuantity} qty / £${newValue.toFixed(2)}` });

      // Check for prior invoices for the *newly selected* items (if any were added or toggled)
      // This will set the content and open the modal if relevant prior invoices exist.
      const relevantPriorInvoices = priorInvoices.filter(inv => {
        const isLotIdCurrentlySelected = currentFilteredItems.some(item => item.lot === inv.lot_id);
        return isLotIdCurrentlySelected; // Filter to show all prior invoices for selected lots
      });

      if (relevantPriorInvoices.length > 0) {
        setPriorInvoiceModalContent(relevantPriorInvoices);
        setShowPriorInvoiceModal(true);
      } else {
        setShowPriorInvoiceModal(false); // Hide modal if no relevant prior invoices for current selection
      }

      return newSelected;
    });
  };

  /**
   * Checks if a specific line item is currently selected.
   * @param {object} item - The item object to check.
   * @returns {boolean} True if the item is selected for the current accumulation, false otherwise.
   */
  const isLineItemSelected = (item) => {
    return selectedLineItems.includes(`${item.sku}-${item.lot}-${item.receivedDate}`);
  };

  /**
   * Checks if all *actionable* items within a given date group are selected.
   * @param {Array<object>} items - An array of item objects for a specific date.
   * @returns {boolean} True if all items in the group that are not previously invoiced are selected, false otherwise.
   */
  const isDateGroupSelected = (items) => {
    // Filter out items that are already previously invoiced
    const actionableItems = items.filter(item => !isItemPreviouslyInvoiced(item));
    // If no actionable items, consider it "selected" if the original group was empty
    if (actionableItems.length === 0) return items.length === 0;
    
    // Check if all actionable items are currently selected for accumulation
    return actionableItems.every(item => isLineItemSelected(item));
  };


  return (
    <Box
      sx={{
        padding: { xs: '16px', md: '32px' },
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Box
        sx={{
          padding: { xs: '24px', md: '32px' },
          width: '100%',
          maxWidth: '960px',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          backgroundColor: 'white',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            textAlign: 'center',
            marginBottom: '24px',
            color: '#374151',
            fontWeight: 600,
          }}
        >
          Invoice Validation Tool
        </Typography>
 <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'center',
          }}
        >
          <TextField
            label="PO Number"
            variant="outlined"
            value={poNumber}
            onChange={handlePoNumberChange}
            fullWidth
            autoFocus
            sx={{
              flexGrow: 1,
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || !poNumber.trim()}
            sx={{
              paddingY: '12px',
              paddingX: '24px',
              minWidth: { xs: '100%', sm: 'auto' },
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
          </Button>
        </Box>
</form>
        {message.text && (
          <Alert
            severity={message.type}
            sx={{
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
              borderRadius: '8px',
            }}
          >
            <AlertTitle sx={{ textTransform: 'capitalize' }}>{message.type}</AlertTitle>
            {message.text}
          </Alert>
        )}

        {/* Prior Invoices Warning Modal */}
        <Dialog
          open={showPriorInvoiceModal}
          onClose={() => setShowPriorInvoiceModal(false)}
          aria-labelledby="prior-invoice-dialog-title"
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)' }
          }}
        >
          <DialogTitle id="prior-invoice-dialog-title" sx={{ backgroundColor: '#ffedd5', color: '#9a3412', fontWeight: 600, borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
            Warning: Selected items have prior invoice records for PO: {processedPoNumber}
          </DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: 'white', padding: '24px' }}>
            <Typography variant="body1" sx={{ marginBottom: '16px', color: '#4a4a4a' }}>
              Some of the currently selected items or their associated Lot IDs have been partially invoiced before.
              Please verify the details below for previous records related to this PO and lot(s):
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
              {priorInvoiceModalContent.map((inv, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    <Box component="strong">Date:</Box> {inv.invoiced_date},
                    <Box component="strong"> Lot ID:</Box> {inv.lot_id},
                    <Box component="strong"> Quantity:</Box> {inv.total_quantity},
                    <Box component="strong"> Value:</Box> £{parseFloat(inv.total_value).toFixed(2)}
                  </Typography>
                </li>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '16px', justifyContent: 'flex-end', backgroundColor: '#f9fafb', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
            <Button onClick={() => setShowPriorInvoiceModal(false)} color="primary" variant="contained" sx={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              Got It
            </Button>
          </DialogActions>
        </Dialog>

        {retailOpsData && (
          <>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                marginTop: '32px',
                marginBottom: '16px',
                color: '#4b5563',
                fontWeight: 500,
              }}
            >
              Product Receipts (PO: {processedPoNumber})
            </Typography>

            {Object.keys(groupedDataByDate).length > 0 ? (
              <>
                {Object.entries(groupedDataByDate).map(([date, items]) => {
                  const dateQuantityTotal = items.reduce((sum, item) => sum + item.quantityReceived, 0);
                  const dateValueTotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
                  // isAllDateItemsSelected checks if all *actionable* items in the group are selected
                  const isAllDateItemsSelected = isDateGroupSelected(items);
                  // Check if all items in the date group are *already previously invoiced*
                  const isDateGroupFullyPreviouslyInvoiced = items.every(isItemPreviouslyInvoiced);

                  return (
                    <Accordion
                      key={date}
                      defaultExpanded={false}
                      sx={{
                        marginBottom: '16px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                        borderRadius: '8px',
                        '&.Mui-expanded': {
                          margin: '16px 0',
                        },
                        '&:before': {
                          display: 'none',
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-${date}-content`}
                        id={`panel-${date}-header`}
                        sx={{
                          backgroundColor: '#f9fafb',
                          borderTopLeftRadius: '8px',
                          borderTopRightRadius: '8px',
                          '&.Mui-expanded': {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            paddingRight: '16px',
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' },
                            gap: { xs: '8px', sm: '0' },
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#4b5563' }}>
                            Received on: {date}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              flexDirection: { xs: 'column', sm: 'row' },
                            }}
                          >
                            <Typography variant="body1">
                              Qty Total: <Box component="span" sx={{ fontWeight: 700 }}>{dateQuantityTotal}</Box>
                            </Typography>
                            <Typography variant="body1">
                              Value Total: <Box component="span" sx={{ fontWeight: 700 }}>£{dateValueTotal.toFixed(2)}</Box>
                            </Typography>
                            <Button
                              variant={isAllDateItemsSelected || isDateGroupFullyPreviouslyInvoiced ? "contained" : "outlined"}
                              color={isAllDateItemsSelected || isDateGroupFullyPreviouslyInvoiced ? "success" : "primary"}
                              size="small"
                              onClick={(e) => { e.stopPropagation(); toggleSelection(items, true); }}
                              disabled={isDateGroupFullyPreviouslyInvoiced || isLoading} // Disable if all items in group previously invoiced or loading
                              startIcon={<CheckCircleOutlineIcon />}
                              sx={{
                                marginLeft: { xs: 0, sm: '8px' },
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
                                '&:hover': {
                                  boxShadow: '0 3px 6px rgba(0, 0, 0, 0.12)',
                                },
                              }}
                            >
                              {isDateGroupFullyPreviouslyInvoiced ? 'Invoiced' : (isAllDateItemsSelected ? 'Uncheck Date' : 'Check Date')}
                            </Button>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          backgroundColor: 'white',
                          borderBottomLeftRadius: '8px',
                          borderBottomRightRadius: '8px',
                        }}
                      >
                        <TableContainer
                          sx={{
                            boxShadow: 'none',
                            borderRadius: '8px',
                          }}
                        >
                          <Table size="small" aria-label="receipt details table">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                                {/* Removed 'Vendor Name' header as requested */}
                                {['SKU', 'LOT', 'Brand Name', 'Product', 'Quantity Received', 'Unit Cost', 'Line Total', 'Action'].map((header) => (
                                  <TableCell key={header} sx={{ fontWeight: 600, color: '#4b5563' }}>
                                    {header}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.map((item, itemIndex) => {
                                const isSelected = isLineItemSelected(item);
                                const isPreviouslyInvoiced = isItemPreviouslyInvoiced(item);

                                return (
                                  <TableRow key={itemIndex}>
                                    {/* Removed 'Vendor Name' cell as requested */}
                                    <TableCell>{item.sku}</TableCell>
                                    <TableCell>{item.lot}</TableCell>
                                    <TableCell>{item.brandName}</TableCell>
                                    <TableCell>{item.productName}</TableCell>
                                    <TableCell>{item.quantityReceived}</TableCell>
                                    <TableCell>£{item.unitCost.toFixed(2)}</TableCell>
                                    <TableCell>£{item.lineTotal.toFixed(2)}</TableCell>
                                    <TableCell>
                                      <Button
                                        variant={isPreviouslyInvoiced || isSelected ? "contained" : "outlined"}
                                        color={isPreviouslyInvoiced || isSelected ? "success" : "primary"}
                                        size="small"
                                        onClick={() => toggleSelection(item)}
                                        disabled={isPreviouslyInvoiced || isLoading} // Disable if previously invoiced or loading
                                        startIcon={<CheckCircleOutlineIcon />}
                                        sx={{
                                          borderRadius: '8px',
                                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
                                          '&:hover': {
                                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.12)',
                                          },
                                        }}
                                      >
                                        {isPreviouslyInvoiced ? 'Invoiced' : (isSelected ? 'Uncheck Line' : 'Check Line')}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {/* PO Grand Total Section */}
                <Box
                  sx={{
                    padding: '16px',
                    marginTop: '24px',
                    borderRadius: '8px',
                    backgroundColor: '#eff6ff',
                    borderLeft: '4px solid #60a5fa',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                    PO Grand Total:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '24px',
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#1e40af' }}>
                      Total Quantity: <Box component="span" sx={{ fontWeight: 800, fontSize: '1.125rem' }}>{grandTotalQuantity}</Box>
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#1e40af' }}>
                      Total Value: <Box component="span" sx={{ fontWeight: 800, fontSize: '1.125rem' }}>£{grandTotalValue.toFixed(2)}</Box>
                    </Typography>
                    <Button
                      // Check if all items in retailOpsData are previously invoiced
                      variant={(selectedLineItems.length === retailOpsData.length && retailOpsData.length > 0) || retailOpsData.every(isItemPreviouslyInvoiced) ? "contained" : "outlined"}
                      color={(selectedLineItems.length === retailOpsData.length && retailOpsData.length > 0) || retailOpsData.every(isItemPreviouslyInvoiced) ? "success" : "primary"}
                      onClick={() => toggleSelection(retailOpsData, true)} // Toggle all items
                      disabled={retailOpsData.every(isItemPreviouslyInvoiced) || isLoading} // Disable if all PO items previously invoiced or loading
                      startIcon={<CheckCircleOutlineIcon />}
                      sx={{
                        paddingY: '8px',
                        paddingX: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      {retailOpsData.every(isItemPreviouslyInvoiced) ? 'Invoiced All' : (selectedLineItems.length === retailOpsData.length && retailOpsData.length > 0 ? 'Uncheck All' : 'Check All')}
                    </Button>
                  </Box>
                </Box>

                {/* Current Invoice Accumulation Section */}
                <Box
                  sx={{
                    padding: '16px',
                    marginTop: '24px',
                    borderRadius: '8px',
                    backgroundColor: '#ecfdf5',
                    borderLeft: '4px solid #34d399',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#047857' }}>
                    Current Invoice Accumulation:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '24px',
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#065f46' }}>
                      Quantity: <Box component="span" sx={{ fontWeight: 800, fontSize: '1.125rem' }}>{currentInvoiceSelected.quantity}</Box>
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#065f46' }}>
                      Value: <Box component="span" sx={{ fontWeight: 800, fontSize: '1.125rem' }}>£{currentInvoiceSelected.value.toFixed(2)}</Box>
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleStoreInvoice}
                      disabled={isLoading || selectedLineItems.length === 0} /* Disable if loading or no items selected */
                      startIcon={<AddShoppingCartIcon />}
                      sx={{
                        paddingY: '8px',
                        paddingX: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Store Invoice'}
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center', color: '#6b7280', marginTop: '16px' }}>
                No product receipts found for this PO number.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
export default InvoiceCheckPage;
